package chand.wordvoyage.platform

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import chand.wordvoyage.config.GoogleSignInConfig
import chand.wordvoyage.data.DetailInfo
import chand.wordvoyage.data.WordEntry
import chand.wordvoyage.repository.AuthRepository
import chand.wordvoyage.repository.AuthState
import chand.wordvoyage.repository.FirestoreRepository
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlin.js.Promise
import kotlin.js.jsTypeOf
import org.w3c.dom.HTMLScriptElement

private const val WEB_AUTH_STORAGE_KEY = "wordvoyage.web.auth"
private const val WEB_AUTH_STORAGE_VERSION = "firebase"
private const val GOOGLE_IDENTITY_SCRIPT_URL = "https://accounts.google.com/gsi/client"
private const val FIREBASE_APP_SCRIPT_URL = "https://www.gstatic.com/firebasejs/10.12.5/firebase-app-compat.js"
private const val FIREBASE_AUTH_SCRIPT_URL = "https://www.gstatic.com/firebasejs/10.12.5/firebase-auth-compat.js"
private const val FIREBASE_FIRESTORE_SCRIPT_URL = "https://www.gstatic.com/firebasejs/10.12.5/firebase-firestore-compat.js"
private const val FIREBASE_API_KEY = "AIzaSyANYgRjpRK5r0JX4eVEIygwsC1ox04qoyE"
private const val FIREBASE_PROJECT_ID = "wordvoyage-c684a"
private const val FIREBASE_AUTH_DOMAIN = "wordvoyage-c684a.firebaseapp.com"
private const val FIREBASE_STORAGE_BUCKET = "wordvoyage-c684a.firebasestorage.app"

private val json = Json { ignoreUnknownKeys = true }
private var currentAuthState: AuthState? = null
private var isGoogleScriptLoading = false
private val pendingGoogleScriptCallbacks = mutableListOf<(Boolean) -> Unit>()

actual fun createAuthRepository(): AuthRepository = object : AuthRepository {
    override fun getCurrentUser(): AuthState = currentAuthState ?: readStoredAuthState().also {
        currentAuthState = it
    }

    override suspend fun signInWithGoogle(idToken: String): Result<AuthState> = runCatching {
        ensureFirebaseInitialized()
        val credential = window.asDynamic().firebase.auth.GoogleAuthProvider.credential(idToken)
        val authResult = window.asDynamic().firebase.auth()
            .signInWithCredential(credential)
            .unsafeCast<Promise<dynamic>>()
            .await()
        val user = authResult.user ?: error("Firebase sign-in failed: no user returned")
        val userId = user.uid as? String ?: error("Firebase sign-in failed: no user id returned")
        val email = user.email as? String ?: getJwtClaim(idToken, "email").orEmpty()
        AuthState.Authenticated(userId = userId, email = email).also { authState ->
            currentAuthState = authState
            storeAuthState(authState)
        }
    }

    override fun signOut() {
        runCatching {
            if (isFirebaseSdkAvailable()) {
                window.asDynamic().firebase.auth().signOut()
            }
        }
        currentAuthState = AuthState.NotAuthenticated
        window.localStorage.removeItem(WEB_AUTH_STORAGE_KEY)
    }

    override suspend fun deleteAccount(): Result<Unit> = runCatching {
            ensureFirebaseInitialized()
        val user = window.asDynamic().firebase.auth().currentUser
        if (user != null) {
            user.delete().unsafeCast<Promise<dynamic>>().await()
        }
        currentAuthState = AuthState.NotAuthenticated
        window.localStorage.removeItem(WEB_AUTH_STORAGE_KEY)
    }
}

actual fun createFirestoreRepository(): FirestoreRepository = object : FirestoreRepository {
    override suspend fun saveWordEntry(userId: String, word: String, detail: DetailInfo): Result<Unit> = runCatching {
        require(userId.isNotBlank()) { "Please sign in before saving history" }
        ensureFirebaseInitialized()

        val entry = js("({})")
        entry.word = word
        entry.meaning = detail.meaning
        entry.howToPronounce = detail.howToPronounce
        entry.wordUsage = detail.wordUsage
        
        // Ensure examples are a plain JS Array
        val examplesArray = js("[]")
        detail.examplesOfUsingWord.forEach { examplesArray.push(it) }
        entry.examplesOfUsingWord = examplesArray
        
        entry.websiteLinkToStillMoreInfo = detail.websiteLinkToStillMoreInfo
        // Use Double (native JS number) for timestamp, as Firestore JS SDK doesn't support Kotlin Long
        entry.savedAt = currentTimeMillis()

        window.asDynamic().firebase.firestore()
            .collection("users")
            .doc(userId)
            .collection("wordHistory")
            .doc(word.lowercase().trim()) // Use word as ID to prevent duplicates
            .set(entry)
            .unsafeCast<Promise<dynamic>>()
            .await()
        println("WordEntry saved to Firestore for web user: $userId, word: $word")
    }.onFailure { e ->
        println("Failed to save word to Firestore: ${e.message}")
    }

    override suspend fun getWordHistory(userId: String): Result<List<WordEntry>> = runCatching {
        if (userId.isBlank()) return@runCatching emptyList()
        ensureFirebaseInitialized()

        val firebase = window.asDynamic().firebase
        val currentUser = firebase.auth().currentUser
        if (currentUser == null) {
            println("Warning: Firebase Auth user is null in getWordHistory. Firestore will likely deny access.")
        } else if (currentUser.uid != userId) {
            println("Warning: Current Firebase User (${currentUser.uid}) does not match requested userId ($userId). Firestore will deny access.")
        }

        val snapshot = firebase.firestore()
            .collection("users")
            .doc(userId)
            .collection("wordHistory")
            .get()
            .unsafeCast<Promise<dynamic>>()
            .await()

        val docs = snapshot.docs
        val count = docs.length.unsafeCast<Int>()
        buildList {
            for (index in 0 until count) {
                val data = docs[index].data()
                add(
                    WordEntry(
                        word = data.word as? String ?: "",
                        meaning = data.meaning as? String ?: "",
                        howToPronounce = data.howToPronounce as? String ?: "",
                        wordUsage = data.wordUsage as? String ?: "",
                        examplesOfUsingWord = jsArrayToStringList(data.examplesOfUsingWord),
                        websiteLinkToStillMoreInfo = data.websiteLinkToStillMoreInfo as? String ?: "",
                        savedAt = jsNumberToLong(data.savedAt)
                    )
                )
            }
        }
    }.onFailure { e ->
        println("Failed to fetch word history from Firestore: ${e.message}")
    }

    override suspend fun deleteWordEntry(userId: String, word: String): Result<Unit> = runCatching {
        require(userId.isNotBlank()) { "User must be signed in" }
        ensureFirebaseInitialized()
        
        window.asDynamic().firebase.firestore()
            .collection("users")
            .doc(userId)
            .collection("wordHistory")
            .doc(word.lowercase().trim())
            .delete()
            .unsafeCast<Promise<dynamic>>()
            .await()
    }

    override suspend fun deleteAllUserData(userId: String): Result<Unit> = runCatching {
        require(userId.isNotBlank()) { "User must be signed in" }
        ensureFirebaseInitialized()
        val firestore = window.asDynamic().firebase.firestore()
        
        val snapshot = firestore
            .collection("users")
            .doc(userId)
            .collection("wordHistory")
            .get()
            .unsafeCast<Promise<dynamic>>()
            .await()
            
        val docs = snapshot.docs
        val count = docs.length.unsafeCast<Int>()
        for (i in 0 until count) {
            docs[i].ref.delete().unsafeCast<Promise<dynamic>>().await()
        }
        
        firestore.collection("users").doc(userId).delete().unsafeCast<Promise<dynamic>>().await()
    }
}

private suspend fun ensureFirebaseInitialized() {
    loadFirebaseScriptsIfNeeded()

    val firebase = window.asDynamic().firebase
    check(jsTypeOf(firebase) != "undefined") {
        "Firebase Web SDK is not loaded. Restart webApp[js] and refresh the browser."
    }

    val apps = firebase.apps
    if (apps == null || apps.length.unsafeCast<Int>() == 0) {
        val config = js("({})")
        config.apiKey = FIREBASE_API_KEY
        config.authDomain = FIREBASE_AUTH_DOMAIN
        config.projectId = FIREBASE_PROJECT_ID
        config.storageBucket = FIREBASE_STORAGE_BUCKET
        firebase.initializeApp(config)
        firebase.firestore().settings(
            js("({ ignoreUndefinedProperties: true })")
        )
    }
}

@Composable
actual fun rememberGoogleSignInLauncher(
    onSuccess: (idToken: String, email: String) -> Unit,
    onFailure: (error: String) -> Unit
): () -> Unit = remember(onSuccess, onFailure) {
    {
        loadGoogleIdentityScript { loaded ->
            if (loaded) {
                showGoogleSignInPrompt(onSuccess, onFailure)
            } else {
                onFailure("Could not load Google Sign-In. Check your internet connection and browser settings.")
            }
        }
    }
}

private fun readStoredAuthState(): AuthState {
    return runCatching {
        val stored = window.localStorage.getItem(WEB_AUTH_STORAGE_KEY) ?: return AuthState.NotAuthenticated
        val versionSeparatorIndex = stored.indexOf('|')
        if (versionSeparatorIndex < 0 || stored.substring(0, versionSeparatorIndex) != WEB_AUTH_STORAGE_VERSION) {
            window.localStorage.removeItem(WEB_AUTH_STORAGE_KEY)
            return AuthState.NotAuthenticated
        }

        val authData = stored.substring(versionSeparatorIndex + 1)
        val separatorIndex = authData.indexOf('|')
        val userId = if (separatorIndex >= 0) authData.substring(0, separatorIndex) else authData
        val email = if (separatorIndex >= 0) authData.substring(separatorIndex + 1) else ""
        if (userId.isBlank()) {
            AuthState.NotAuthenticated
        } else {
            AuthState.Authenticated(
                userId = userId,
                email = email
            )
        }
    }.getOrElse { error ->
        println("Failed to read stored auth state: ${error.message}")
        AuthState.NotAuthenticated
    }
}

private suspend fun loadFirebaseScriptsIfNeeded() {
    if (isFirebaseSdkAvailable()) return

    loadScript(FIREBASE_APP_SCRIPT_URL).await()
    loadScript(FIREBASE_AUTH_SCRIPT_URL).await()
    loadScript(FIREBASE_FIRESTORE_SCRIPT_URL).await()
}

private fun loadScript(src: String): Promise<Unit> = Promise { resolve, reject ->
    runCatching {
        val script = document.createElement("script") as HTMLScriptElement
        script.src = src
        script.asDynamic().async = true
        script.asDynamic().defer = true
        script.asDynamic().onload = { resolve(Unit) }
        script.asDynamic().onerror = {
            reject(Exception("Failed to load Firebase script: $src"))
        }
        (document.head ?: document.body ?: error("No document root available to load Firebase scripts"))
            .appendChild(script)
    }.onFailure { error ->
        reject(Exception(error.message ?: "Failed to load Firebase script: $src"))
    }
}

private fun storeAuthState(authState: AuthState.Authenticated) {
    runCatching {
        window.localStorage.setItem(WEB_AUTH_STORAGE_KEY, "$WEB_AUTH_STORAGE_VERSION|${authState.userId}|${authState.email}")
    }.onFailure { error ->
        println("Failed to store auth state: ${error.message}")
    }
}

private fun isFirebaseSdkAvailable(): Boolean = runCatching {
    js("typeof firebase !== 'undefined' && !!firebase.auth && !!firebase.firestore").unsafeCast<Boolean>()
}.getOrDefault(false)

private fun jsArrayToStringList(value: dynamic): List<String> {
    if (value == null || jsTypeOf(value.length) == "undefined") return emptyList()
    val count = value.length.unsafeCast<Int>()
    return buildList {
        for (index in 0 until count) {
            (value[index] as? String)?.let { add(it) }
        }
    }
}

private fun jsNumberToLong(value: dynamic): Long = when (jsTypeOf(value)) {
    "number" -> value.unsafeCast<Double>().toLong()
    "string" -> (value as String).toLongOrNull() ?: 0L
    "object" -> {
        // Handle Firestore Timestamp object
        if (value != null && jsTypeOf(value.seconds) != "undefined") {
            (value.seconds as Double).toLong() * 1000L
        } else 0L
    }
    else -> 0L
}

private fun getJwtClaim(idToken: String, claimName: String): String? {
    return runCatching {
        val firstDotIndex = idToken.indexOf('.')
        if (firstDotIndex < 0) return null
        val secondDotIndex = idToken.indexOf('.', startIndex = firstDotIndex + 1)
        val payload = if (secondDotIndex > firstDotIndex) {
            idToken.substring(firstDotIndex + 1, secondDotIndex)
        } else {
            idToken.substring(firstDotIndex + 1)
        }
        if (payload.isBlank()) return null

        val normalized = payload.replace('-', '+').replace('_', '/')
        val padded = normalized.padEnd(normalized.length + (4 - normalized.length % 4) % 4, '=')
        val payloadJson = window.asDynamic().atob(padded).unsafeCast<String>()
        val payloadObject = json.parseToJsonElement(payloadJson).jsonObject
        payloadObject[claimName]?.jsonPrimitive?.contentOrNull
    }.getOrElse { error ->
        println("Failed to decode JWT claim '$claimName': ${error.message}")
        null
    }
}

private fun loadGoogleIdentityScript(onComplete: (Boolean) -> Unit) {
    if (isGoogleIdentityAvailable()) {
        onComplete(true)
        return
    }

    pendingGoogleScriptCallbacks += onComplete
    if (isGoogleScriptLoading) return

    isGoogleScriptLoading = true
    runCatching {
        val script = document.createElement("script") as HTMLScriptElement
        script.src = GOOGLE_IDENTITY_SCRIPT_URL
        script.asDynamic().async = true
        script.asDynamic().defer = true
        script.asDynamic().onload = { completeGoogleScriptLoading(true) }
        script.asDynamic().onerror = { completeGoogleScriptLoading(false) }
        (document.head ?: document.body ?: error("No document root available to load Google Identity script"))
            .appendChild(script)
    }.onFailure { error ->
        println("Failed to load Google Identity script: ${error.message}")
        completeGoogleScriptLoading(false)
    }
}

private fun completeGoogleScriptLoading(loaded: Boolean) {
    isGoogleScriptLoading = false
    val callbacks = pendingGoogleScriptCallbacks.toList()
    pendingGoogleScriptCallbacks.clear()
    callbacks.forEach { it(loaded && isGoogleIdentityAvailable()) }
}

private fun showGoogleSignInPrompt(
    onSuccess: (idToken: String, email: String) -> Unit,
    onFailure: (error: String) -> Unit
) {
    if (!isGoogleIdentityAvailable()) {
        onFailure("Google Sign-In is not available in this browser")
        return
    }

    val callback: (dynamic) -> Unit = { response ->
        val credential = response.credential as? String
        if (credential.isNullOrBlank()) {
            onFailure("Google Sign-In did not return an ID token")
        } else {
            onSuccess(credential, getJwtClaim(credential, "email").orEmpty())
        }
    }

    val config = js("({})")
    config.client_id = GoogleSignInConfig.WEB_CLIENT_ID
    config.callback = callback
    config.auto_select = false
    config.cancel_on_tap_outside = true
    config.use_fedcm_for_prompt = true

    runCatching {
        val googleAccountsId = window.asDynamic().google.accounts.id
        googleAccountsId.initialize(config)

        val promptCallback: (dynamic) -> Unit = { notification ->
            val notDisplayed = callBooleanJsMethod(notification, "isNotDisplayed")
            val skipped = callBooleanJsMethod(notification, "isSkippedMoment")
            if (notDisplayed || skipped) {
                val reason = when {
                    notDisplayed -> callStringJsMethod(notification, "getNotDisplayedReason")
                    skipped -> callStringJsMethod(notification, "getSkippedReason")
                    else -> null
                }
                onFailure(
                    reason?.let { "Google Sign-In prompt was not shown: $it" }
                        ?: "Google Sign-In prompt was dismissed or unavailable"
                )
            }
        }
        googleAccountsId.prompt(promptCallback)
    }.onFailure { error ->
        onFailure("Google Sign-In could not start: ${error.message}")
    }
}

private fun isGoogleIdentityAvailable(): Boolean =
    js("typeof google !== 'undefined' && !!(google.accounts && google.accounts.id)").unsafeCast<Boolean>()

private fun callBooleanJsMethod(target: dynamic, methodName: String): Boolean {
    val method = target[methodName]
    return jsTypeOf(method) == "function" && method.call(target) == true
}

private fun callStringJsMethod(target: dynamic, methodName: String): String? {
    val method = target[methodName]
    return if (jsTypeOf(method) == "function") method.call(target) as? String else null
}

private fun currentTimeMillis(): Double = js("Date.now()").unsafeCast<Double>()

